import React, { createContext, useEffect, useReducer } from 'react';
import { register, GoogleRegister } from '../actions/authAction';
import { useHistory } from 'react-router-dom';
import Cookie from '../utils/Cookie';
import SplashScreen from '../components/SplashScreen';
import ToastHot from 'react-hot-toast';
import CallAPI from '../utils/APICall';
import { jwtDecode } from "jwt-decode"
import { lOGIN_API, ME_API, LOGOUT_API } from '../utils/APIEndPoints';

const initialAuthState = {
  isAuthenticated: false,
  isLogout: false,
  isInitialised: false,
  user: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user: user
      };
    }
    case 'LOGIN': {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    case 'USER_UPDATE': {
      return {
        ...state,
        user: action.payload,
      };
    }
    case 'REGISTER': {
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  login: () => Promise.resolve(),
  logout: () => { },
  updateUser: () => Promise.resolve(),
  RegisterUser: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const history = useHistory();
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const login = async (username, password) => {
    let body = {
      username: username,
      password: password
    }
    
    const response = await CallAPI("POST", lOGIN_API, body);
    if (response.status) {
      // Store token in localStorage
      const token = response.res.data.token;
      if (token) {
        localStorage.setItem('accessToken', token);
      }
      
      let user = response.res.data.userOrCompany
      if (response.res.data.userOrCompany.userType === "superAdmin") {
        user.totalCompanies = response.res.data.totalCompanies
      }
      dispatch({ type: 'LOGIN', payload: user })
      return { loginData: user, response: true };
    } else {
      return { loginData: response.msg, response: false };
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint
      await CallAPI("POST", LOGOUT_API);
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear localStorage — including the member-verification token, which
    // previously persisted across sessions and silently overrode the admin
    // token on subsequent calls.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('accessRole');
    localStorage.removeItem('memberVerificationToken');
    localStorage.removeItem('userObj');
    // Avoid sessionStorage.clear() to prevent stomping on unrelated state
    // owned by other apps sharing the origin.
    sessionStorage.removeItem('api_docs_api_key');
    sessionStorage.removeItem('api_docs_access_token');
    
    dispatch({ type: 'LOGOUT' });
    history.push('/login');
  };

  const updateUser = (user) => {
    dispatch({ type: 'USER_UPDATE', payload: user })
  }

  const RegisterUser = async (payload, type = 'normalRegister') => {
    if (type === 'normalRegister') {
      const response = await register(payload);
      if (response) {
        if (response.data.status === 200) {
          return response;
        } else {
          return response;
        }
      } else {
        ToastHot.error("Something went wrong");
      }
    } else {
      const response = await GoogleRegister(payload);
      if (response) {
        if (response.data.status === 200) {
          ToastHot.success("Successfully registered with us");
          Cookie.setCookie('_tokenVAS', response.data.user_data.token_data.access_tokenVAS, 1);
          dispatch({ type: 'REGISTER', payload: response.data.user_data.user })
          return response;
        } else {
          return response;
        }
      } else {
        ToastHot.error("Something went wrong");
      }
    }
  };

  // Remove setSession function as we no longer use localStorage for tokens

  // Remove isValidToken function as we can't access httpOnly cookies from JavaScript

  useEffect(() => {
    const initialise = async () => {
      try {
        // Check if token exists in localStorage
        const token = localStorage.getItem('accessToken');
        
        if (token) {
          // Check if user is authenticated by calling the /me endpoint
          const response = await CallAPI("GET", ME_API);

          if (response.status && response.res.data.userOrCompany) {
            let user = response.res.data.userOrCompany;
            if (response.res.data.userOrCompany.userType === "superAdmin") {
              user.totalCompanies = response.res.data.totalCompanies
            }
            dispatch({
              type: 'INITIALISE',
              payload: {
                isAuthenticated: true,
                isLogout: false,
                user
              }
            });
          } else {
            // Token exists but authentication failed, clear it
            localStorage.removeItem('accessToken');
            dispatch({
              type: 'INITIALISE',
              payload: {
                isAuthenticated: false,
                user: null,
              }
            });
          }
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: false,
              user: null,
            }
          });
        }
      } catch (err) {
        console.error('Initialization error:', err);
        localStorage.removeItem('accessToken');
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null,
          }
        });
      }
    };
    initialise();
  }, []); // Empty dependency array - this should only run once on mount

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        updateUser,
        RegisterUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
